import {
  ScheduledWorkflow,
  WorkflowJob,
} from "@/reducers/openclawWorkflowsSlice"
import { Fuel, FileText, MessageSquare, Eye, MapPin } from "lucide-react"
import { useTranslation } from "react-i18next"
import { phaseOf } from "@/lib/stopAutomations"
import { formatSchedule } from "@/lib/formatWorkflowSchedule"
import { useIsDriverWorkspace } from "@/contexts/DriverWorkspaceContext"
import { useAppDispatch } from "@/reducers/hooks"
import { showTransportBriefViewer } from "@/reducers/overlaysSlice"
import { cn } from "@/lib/utils"

/**
 * The vocabulary both automations surfaces share: what kind of rule this is,
 * how a row looks, and the stop-automation shortcut. The global Slick AI list
 * groups rules by who owns them; a driver room groups them by who they are
 * about — but a row is a row on both.
 */

/** A one-shot rule pinned to a route stop — edited with the stop form. */
export const isStopRule = (job: ScheduledWorkflow): boolean =>
  phaseOf(job) !== undefined

/**
 * A plain schedule — edited with the cron form. Deliberately excludes every
 * other `event` rule (geofence, fuel norms): they share the kind but not the
 * shape, and `cron_update` would rewrite their trigger into a cron.
 */
export const isScheduleRule = (job: ScheduledWorkflow): boolean =>
  job.schedule?.kind === "cron" || job.schedule?.kind === "at"

/** A zone-entry/exit trigger that isn't a stop arrival/departure. */
export const isGeofenceRule = (job: ScheduledWorkflow): boolean =>
  job.schedule?.kind === "event" && !isStopRule(job)

export const isEditable = (job: ScheduledWorkflow): boolean =>
  isStopRule(job) || isScheduleRule(job)

/**
 * No structured field marks a built-in handler's kind, so those two are read
 * off the name — same as `BriefingSection` already hardcodes "Transport
 * brief" for the one it links to directly. Everything else gets an icon for
 * its trigger type instead, which the schedule shape does tell us.
 */
const iconFor = (job: ScheduledWorkflow) => {
  const lower = job.name.toLowerCase()
  if (lower.includes("fuel")) return Fuel
  if (lower.includes("brief")) return FileText
  if (isGeofenceRule(job)) return Eye
  if (isStopRule(job)) return MapPin
  if (isScheduleRule(job)) return MessageSquare
  return null
}

export function sortActiveFirst(jobs: WorkflowJob[]): WorkflowJob[] {
  return [...jobs].sort((a, b) => {
    if (a.status === b.status) return 0
    return a.status === "active" ? -1 : 1
  })
}

export const WorkflowItem = ({
  job,
  onEdit,
  layout = "inline",
}: {
  job: ScheduledWorkflow
  onEdit?: (job: ScheduledWorkflow) => void
  /** "stacked" puts the schedule on its own line under the name, as the
   * driver-room prototype wants it, instead of right-aligned on the same
   * line as the account-wide list has always shown it. */
  layout?: "inline" | "stacked"
}) => {
  const { t } = useTranslation()
  // Paused workflows are dimmed as a visual hint, but the schedule text is
  // exempted from the dim — for already-fired one-shots the timestamp IS the
  // information operators come back to see, so it must stay readable.
  const dim = job.status === "paused" ? "opacity-40" : ""
  // Trigger-type icons arrived with the workspace; the plain list keeps the
  // bare name it has always shown.
  const Icon = useIsDriverWorkspace() ? iconFor(job) : null
  const scheduleText = formatSchedule(job.schedule, job.status, t)

  if (layout === "stacked") {
    const content = (
      <>
        {Icon && (
          <Icon
            className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground"
            aria-hidden
          />
        )}
        <span className="min-w-0 flex-1">
          <span
            className={cn(
              "block truncate text-sm font-medium text-foreground",
              dim,
            )}
          >
            {job.name}
          </span>
          {scheduleText && (
            <span className="block text-xs text-muted-foreground">
              {scheduleText}
            </span>
          )}
        </span>
      </>
    )
    if (!onEdit) {
      return (
        <div className="-mx-2 flex items-start gap-3 rounded-md px-2 py-1.5 hover:bg-muted/60">
          {content}
        </div>
      )
    }
    return (
      <button
        type="button"
        onClick={() => onEdit(job)}
        title={t("ai.automations.edit")}
        className="-mx-2 flex w-[calc(100%+1rem)] items-start gap-3 rounded-md px-2 py-1.5 text-left transition-colors hover:bg-muted/60 hover:text-base-primary"
      >
        {content}
      </button>
    )
  }

  const label = (
    <span className={`min-w-0 flex-1 truncate text-sm text-foreground ${dim}`}>
      {job.name}
    </span>
  )
  const name = Icon ? (
    <span className="flex min-w-0 flex-1 items-center gap-2">
      <Icon className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
      {label}
    </span>
  ) : (
    label
  )
  const schedule = (
    <span className="shrink-0 text-xs text-muted-foreground">
      {scheduleText}
    </span>
  )

  // Only rules this panel can actually write are clickable — a row that opens
  // a form unable to express its trigger would be worse than a plain row.
  if (!onEdit) {
    return (
      <div className="flex items-center gap-3 py-1">
        {name}
        {schedule}
      </div>
    )
  }

  return (
    <button
      type="button"
      onClick={() => onEdit(job)}
      title={t("ai.automations.edit")}
      className="flex items-center gap-3 py-1 text-left transition-colors hover:text-base-primary"
    >
      {name}
      {schedule}
    </button>
  )
}

export const ScheduledGroup = ({
  label,
  jobs,
  onEdit,
}: {
  label: string
  jobs: ScheduledWorkflow[]
  onEdit?: (job: ScheduledWorkflow) => void
}) => {
  if (jobs.length === 0) return null
  return (
    <section>
      <p className="mb-1 text-xs font-medium text-muted-foreground">{label}</p>
      <div className="flex flex-col">
        {(sortActiveFirst(jobs) as ScheduledWorkflow[]).map((job) => (
          <WorkflowItem
            key={job.id}
            job={job}
            onEdit={onEdit && isEditable(job) ? onEdit : undefined}
          />
        ))}
      </div>
    </section>
  )
}

/**
 * The stop-automation shortcut, shown until the driver has a stop rule —
 * once there is one, its row above is the way to manage it. Only a driver
 * room offers it: the global Slick AI sidebar has no driver to attach to.
 */
export const AddStopAutomationLink = ({
  onAddStopAutomation,
  jobs,
}: {
  onAddStopAutomation: () => void
  jobs: ScheduledWorkflow[]
}) => {
  const { t } = useTranslation()
  return jobs.some(isStopRule) ? null : (
    <button
      type="button"
      onClick={onAddStopAutomation}
      className="py-1 text-sm text-base-primary hover:underline"
    >
      {t("ai.automations.addForStop")}
    </button>
  )
}

/** Public-read S3 URL of the composite transport-brief digest. Matches the URL
 * the slickagent-pi `get_daily_transport_brief` tool emits, so the same
 * `TransportBriefDialog` renders it. */
export const TRANSPORT_BRIEF_DIGEST_URL =
  "https://slickagent-pi-news.s3.eu-central-1.amazonaws.com/transport-brief/digest/latest.md"

/**
 * The daily transport digest. It is written for the whole fleet, so it lands
 * under the fleet-wide heading in a driver room and under "Briefing" in the
 * account-wide panel — one row, two places that each say where it comes from.
 */
export const TransportBriefRow = ({
  subtitle,
}: {
  /** Stacks a second line under the name, e.g. "Daily digest" — the driver
   * room prototype wants this; the account-wide list keeps its plain
   * single-line row when omitted. */
  subtitle?: string
} = {}) => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  return (
    <button
      type="button"
      onClick={() =>
        dispatch(showTransportBriefViewer({ url: TRANSPORT_BRIEF_DIGEST_URL }))
      }
      className={cn(
        "flex w-full items-center gap-2 py-1 text-left text-sm text-foreground transition-colors hover:text-base-primary",
        subtitle && "items-start py-1.5",
      )}
    >
      <FileText
        className={cn(
          "h-4 w-4 shrink-0 text-muted-foreground",
          subtitle && "mt-0.5",
        )}
      />
      {subtitle ? (
        <span className="min-w-0 flex-1">
          <span className="block truncate font-medium">
            {t("ai.automations.transportBrief")}
          </span>
          <span className="block text-xs font-normal text-muted-foreground">
            {subtitle}
          </span>
        </span>
      ) : (
        <span className="min-w-0 flex-1 truncate">
          {t("ai.automations.transportBrief")}
        </span>
      )}
    </button>
  )
}
